import React, {
  useState,
  useMemo,
  useCallback,
  createContext,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DeviceSnapshot } from '@/components/device/DeviceSnapshot';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Plus,
  Camera,
  Volume2,
  Zap,
  Search,
  ChevronDown,
  Pencil,
  GripVertical,
} from 'lucide-react';
import { DeviceData } from '../DevicesTab';

// Create a context for camera selection
interface SelectionContextType {
  selectedCameras: Set<string>;
  setSelectedCameras: React.Dispatch<React.SetStateAction<Set<string>>>;
  allCameras: DeviceData[];
}

const SelectionContext = createContext<SelectionContextType>({
  selectedCameras: new Set(),
  setSelectedCameras: () => {},
  allCameras: [],
});

type DeviceCategory = 'camera' | 'speaker' | 'relay';

interface GroupedDevices {
  groupId: string;
  nvrId?: string;
  nvrName: string;
  devices: DeviceData[];
}

interface UnmonitoredDevicesSidebarProps {
  siteId: string;
  companyId: string;
  cameras: DeviceData[];
  speakers: DeviceData[];
  relays: DeviceData[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export const UnmonitoredDevicesSidebar: React.FC<UnmonitoredDevicesSidebarProps> = ({
  siteId,
  companyId,
  cameras,
  speakers,
  relays,
  searchQuery,
  setSearchQuery,
}) => {
  const [collapsedNvrs, setCollapsedNvrs] = useState<Set<string>>(new Set());
  const [hoveredCamera, setHoveredCamera] = useState<string | null>(null);
  const [hoveredCameraData, setHoveredCameraData] = useState<{
    camera: DeviceData;
    position: { x: number; y: number };
  } | null>(null);
  const [selectedCameras, setSelectedCameras] = useState<Set<string>>(new Set());
  const initializedGroupsRef = useRef<Set<string>>(new Set());

  const selectionContextValue = useMemo(() => ({
    selectedCameras,
    setSelectedCameras,
    allCameras: cameras,
  }), [selectedCameras, setSelectedCameras, cameras]);

  // Toggle NVR section collapse/expand
  const toggleNvrCollapse = (groupId: string) => {
    setCollapsedNvrs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Handle camera hover with position tracking
  const handleCameraHover = useCallback((camera: DeviceData, element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    
    setHoveredCameraData({
      camera,
      position: {
        x: rect.right + 10, // 10px to the right of the card
        y: rect.top + (rect.height / 2) - 80 // Center vertically with some offset
      }
    });
    setHoveredCamera(camera.id);
  }, []);

  const handleCameraLeave = useCallback(() => {
    setHoveredCamera(null);
    setHoveredCameraData(null);
  }, []);

  // Helper functions for filtering and grouping
  const sortDevicesAlphabetically = (devices: DeviceData[]) => {
    return [...devices].sort((a, b) => a.name.localeCompare(b.name));
  };

  const filterDevicesBySearch = (devices: DeviceData[], query: string) => {
    if (!query.trim()) return devices;
    const lowerQuery = query.toLowerCase();
    return devices.filter(device => 
      device.name.toLowerCase().includes(lowerQuery) ||
      device.assetName?.toLowerCase().includes(lowerQuery) ||
      device.nvrName?.toLowerCase().includes(lowerQuery)
    );
  };

  const getGroupId = (device: DeviceData, deviceType: DeviceCategory) => {
    if (device.nvrId) return `${deviceType}-${device.nvrId}`;
    if (device.componentGroupId) return `${deviceType}-${device.componentGroupId}`;
    if (device.nvrName) return `${deviceType}-${device.nvrName}`;
    return `${deviceType}-device-${device.id}`;
  };

  const groupDevicesByNvr = (devices: DeviceData[], deviceType: DeviceCategory): GroupedDevices[] => {
    const filteredAndSorted = sortDevicesAlphabetically(filterDevicesBySearch(devices, searchQuery));

    const grouped = new Map<string, GroupedDevices>();

    filteredAndSorted.forEach(device => {
      const groupId = getGroupId(device, deviceType);
      const existingGroup = grouped.get(groupId);

      if (existingGroup) {
        existingGroup.devices.push(device);
      } else {
        grouped.set(groupId, {
          groupId,
          nvrId: device.nvrId,
          nvrName: device.nvrName || 'Unknown NVR',
          devices: [device],
        });
      }
    });

    return Array.from(grouped.values())
      .map(group => ({
        ...group,
        devices: sortDevicesAlphabetically(group.devices),
      }))
      .sort((a, b) => a.nvrName.localeCompare(b.nvrName));
  };

  // Group devices by NVR
  const groupedCameras = useMemo(() => groupDevicesByNvr(cameras, 'camera'), [cameras, searchQuery]);
  const groupedSpeakers = useMemo(() => groupDevicesByNvr(speakers, 'speaker'), [speakers, searchQuery]);
  const groupedRelays = useMemo(() => groupDevicesByNvr(relays, 'relay'), [relays, searchQuery]);

  useEffect(() => {
    const groupIds = [
      ...groupedCameras.map(group => group.groupId),
      ...groupedSpeakers.map(group => group.groupId),
      ...groupedRelays.map(group => group.groupId),
    ];

    if (groupIds.length === 0) {
      return;
    }

    setCollapsedNvrs(prev => {
      let hasChanges = false;
      const next = new Set(prev);

      groupIds.forEach(groupId => {
        if (!initializedGroupsRef.current.has(groupId)) {
          initializedGroupsRef.current.add(groupId);
          if (!next.has(groupId)) {
            next.add(groupId);
            hasChanges = true;
          }
        }
      });

      return hasChanges ? next : prev;
    });
  }, [groupedCameras, groupedSpeakers, groupedRelays]);

  const totalCameras = groupedCameras.reduce((total, group) => total + group.devices.length, 0);
  const totalSpeakers = groupedSpeakers.reduce((total, group) => total + group.devices.length, 0);
  const totalRelays = groupedRelays.reduce((total, group) => total + group.devices.length, 0);

  return (
    <SelectionContext.Provider value={selectionContextValue}>
      <div className="w-80 bg-[#1a1a1a] border-r border-gray-700 flex-shrink-0 h-full flex flex-col">
        <div className="p-4 overflow-y-auto flex-1">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2 gap-3">
            <h3 className="text-white font-semibold">Unmonitored Devices</h3>
            <Button
              size="xs"
              className="h-7 px-2 text-xs"
              onClick={() => {
                const url = `/devices/customer/${companyId}/add-device?siteId=${siteId}`;
                window.location.href = url;
              }}
              title="Add new device"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Device
            </Button>
          </div>
          <p className="text-xs mt-1">
            Drag to zones to enable monitoring
            {selectedCameras.size > 0 && (
              <span className="ml-2 text-blue-400">
                ({selectedCameras.size} selected)
              </span>
            )}
          </p>

          {/* Search Input */}
          <div className="relative mt-3">
            <Input
              placeholder="Search devices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <Accordion type="multiple" defaultValue={[]} className="w-full">
          {/* Cameras Section */}
          <AccordionItem value="cameras" className="border-gray-700">
            <AccordionTrigger className="text-white hover:text-gray-300 py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <Camera className="h-4 w-4 text-blue-400" />
                <span>Cameras ({totalCameras})</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              {groupedCameras.length === 0 ? (
                <p className="text-gray-400 text-sm">
                  {searchQuery ? 'No cameras found matching search' : 'No available cameras'}
                </p>
              ) : (
                <div className="space-y-3">
                  {groupedCameras.map(group => (
                    <NvrGroup
                      key={group.groupId}
                      groupId={group.groupId}
                      nvrName={group.nvrName}
                      devices={group.devices}
                      deviceType="camera"
                      isCollapsed={collapsedNvrs.has(group.groupId)}
                      onToggleCollapse={() => toggleNvrCollapse(group.groupId)}
                      hoveredCamera={hoveredCamera}
                      onCameraHover={handleCameraHover}
                      onCameraLeave={handleCameraLeave}
                    />
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Speakers Section */}
          <AccordionItem value="speakers" className="border-gray-700">
            <AccordionTrigger className="text-white hover:text-gray-300 py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <Volume2 className="h-4 w-4 text-green-400" />
                <span>Speakers ({totalSpeakers})</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              {groupedSpeakers.length === 0 ? (
                <p className="text-gray-400 text-sm">
                  {searchQuery ? 'No speakers found matching search' : 'No available speakers'}
                </p>
              ) : (
                <div className="space-y-3">
                  {groupedSpeakers.map(group => (
                    <NvrGroup
                      key={group.groupId}
                      groupId={group.groupId}
                      nvrName={group.nvrName}
                      devices={group.devices}
                      deviceType="speaker"
                      isCollapsed={collapsedNvrs.has(group.groupId)}
                      onToggleCollapse={() => toggleNvrCollapse(group.groupId)}
                    />
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Relays Section */}
          <AccordionItem value="relays" className="border-gray-700">
            <AccordionTrigger className="text-white hover:text-gray-300 py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-400" />
                <span>Relays ({totalRelays})</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              {groupedRelays.length === 0 ? (
                <p className="text-gray-400 text-sm">
                  {searchQuery ? 'No relays found matching search' : 'No available relays'}
                </p>
              ) : (
                <div className="space-y-3">
                  {groupedRelays.map(group => (
                    <NvrGroup
                      key={group.groupId}
                      groupId={group.groupId}
                      nvrName={group.nvrName}
                      devices={group.devices}
                      deviceType="relay"
                      isCollapsed={collapsedNvrs.has(group.groupId)}
                      onToggleCollapse={() => toggleNvrCollapse(group.groupId)}
                    />
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        </div>

        {/* Camera Hover Preview Popout */}
        {hoveredCameraData && (
          <div
            className="fixed z-50 bg-gray-800/95 backdrop-blur-sm border border-gray-600 rounded-lg p-3 pointer-events-none"
            style={{
              left: hoveredCameraData.position.x,
              top: hoveredCameraData.position.y,
            }}
          >
            <div className="flex flex-col space-y-2">
              <p className="text-white text-sm font-medium truncate">
                {hoveredCameraData.camera.name}
              </p>
              <div className="w-80 h-60 overflow-hidden rounded border border-gray-600">
                <DeviceSnapshot
                  deviceId={hoveredCameraData.camera.id}
                  className="w-full h-full object-cover"
                  withLoadingText={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </SelectionContext.Provider>
  );
};

interface NvrGroupProps {
  groupId: string;
  nvrName: string;
  devices: DeviceData[];
  deviceType: 'camera' | 'speaker' | 'relay';
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  hoveredCamera?: string | null;
  onCameraHover?: (camera: DeviceData, element: HTMLElement) => void;
  onCameraLeave?: () => void;
}

const NvrGroup: React.FC<NvrGroupProps> = ({
  nvrName,
  devices,
  deviceType,
  isCollapsed,
  onToggleCollapse,
  hoveredCamera,
  onCameraHover,
  onCameraLeave,
}) => {
  // Get nvrId from first device in the group
  const nvrId = devices[0]?.nvrId;

  // Access selectedCameras from context
  const { selectedCameras, setSelectedCameras } = useContext(SelectionContext);
  const dragInProgressRef = useRef(false);

  const handleGroupDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (deviceType !== 'camera') {
      return;
    }

    dragInProgressRef.current = true;
    
    // Use the same structure as individual camera selection drag
    const dragData = {
      deviceType: 'multi-camera' as const,
      selectedCameraIds: devices.map(device => device.id),
      devices,
      count: devices.length,
    };


    event.dataTransfer.setData('application/json', JSON.stringify(dragData));
    event.dataTransfer.effectAllowed = 'copy';
  };

  const handleGroupDragEnd = () => {
    dragInProgressRef.current = false;
  };

  const handleToggleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (dragInProgressRef.current) {
      dragInProgressRef.current = false;
      return;
    }
    onToggleCollapse();
  };

  return (
    <div className="border rounded-lg p-3 bg-card2">
      <div
        className="flex items-center justify-between cursor-pointer mb-2 pl-1"
        data-testid="nvr-group-header"
        onClick={handleToggleClick}
        draggable={deviceType === 'camera' && devices.length > 0}
        onDragStart={handleGroupDragStart}
        onDragEnd={handleGroupDragEnd}
      >
        <div className="flex flex-col gap-1 flex-1">
          <div className="text-xs font-medium" title={nvrName}>
            {nvrName}
          </div>
          {deviceType === 'camera' && (
            <div className="text-[10px] uppercase tracking-wide text-gray-400">
              {devices.length} {devices.length === 1 ? 'unmonitored camera' : 'unmonitored cameras'}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {nvrId && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const url = `/devices/${nvrId}/edit`;
                window.location.href = url;
              }}
              className="h-6 w-6 hover:text-gray-200 transition-colors flex items-center justify-center rounded hover:bg-gray-600/50"
              title="Edit NVR"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
          <ChevronDown 
            className={`h-4 w-4 transition-transform duration-200 ${
              isCollapsed ? '' : 'rotate-180'
            }`} 
          />
        </div>
      </div>
      {!isCollapsed && (
        <div className="space-y-2">
          {devices.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              deviceType={deviceType}
              hoveredCamera={hoveredCamera}
              onCameraHover={onCameraHover}
              onCameraLeave={onCameraLeave}
              isSelected={deviceType === 'camera' ? selectedCameras.has(device.id) : false}
              onSelectionChange={deviceType === 'camera' ? (selected) => {
                setSelectedCameras(prev => {
                  const newSet = new Set(prev);
                  if (selected) {
                    newSet.add(device.id);
                  } else {
                    newSet.delete(device.id);
                  }
                  return newSet;
                });
              } : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface DeviceCardProps {
  device: DeviceData;
  deviceType: 'camera' | 'speaker' | 'relay';
  hoveredCamera?: string | null;
  onCameraHover?: (camera: DeviceData, element: HTMLElement) => void;
  onCameraLeave?: () => void;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
}

const DeviceCard: React.FC<DeviceCardProps> = ({
  device,
  deviceType,
  hoveredCamera,
  onCameraHover,
  onCameraLeave,
  isSelected,
  onSelectionChange
}) => {
  const isHovered = deviceType === 'camera' && hoveredCamera === device.id;
  const { selectedCameras, setSelectedCameras, allCameras } = useContext(SelectionContext);

  const handleDragStart = (e: React.DragEvent) => {
    // For cameras, include selected cameras if this camera is selected
    let dragData;
    if (deviceType === 'camera' && selectedCameras.has(device.id) && selectedCameras.size > 1) {
      // Multi-camera drag - find all selected camera data
      const selectedCameraData = allCameras.filter(cam => selectedCameras.has(cam.id));
      dragData = {
        deviceType: 'multi-camera',
        selectedCameraIds: Array.from(selectedCameras),
        devices: selectedCameraData,
        count: selectedCameras.size
      };
    } else {
      // Single device drag
      dragData = {
        deviceId: device.id,
        deviceName: device.name,
        deviceType,
        device: device,
      };
    }
    
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = () => {
    // Drag completed
  };

  const handleClick = (e: React.MouseEvent) => {
    if (deviceType === 'camera' && onSelectionChange) {
      if (e.shiftKey) {
        // Toggle selection with shift+click
        onSelectionChange(!isSelected);
      } else {
        // Regular click - clear other selections and select only this one
        setSelectedCameras(new Set([device.id]));
      }
    }
  };

  return (
    <div
      className={`flex items-center border rounded cursor-move transition-colors ${
        isSelected ? 'border-blue-400 bg-blue-400/20' : 'border-gray-400/30'
      } ${
        isHovered ? 'bg-gray-400/20' : isSelected ? 'bg-blue-400/20' : 'bg-gray-400/10'
      } hover:bg-gray-400/20`}
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={(e) => {
        if (deviceType === 'camera' && onCameraHover) {
          onCameraHover(device, e.currentTarget);
        }
      }}
      onMouseLeave={() => {
        if (deviceType === 'camera' && onCameraLeave) {
          onCameraLeave();
        }
      }}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0 p-2">
        <span className="text-white text-sm font-medium truncate">{device.name}</span>
      </div>
      <div className="flex items-center p-2">
        <div className="h-4 w-4 text-gray-400 flex-shrink-0">
          <GripVertical className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
};